import { trim } from 'lodash'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { BizType } from '@/constant'
import { ACTION_TYPE, BIZ_TYPE } from '@/constant'
import Api from '@/services/http'
import { customTagUrl } from '@/services/url'
import type { AppDispatch } from '@/store'
import { uuid } from '@/utils'
import type * as Types from './types'

export const StateTagListMap = {
  [BIZ_TYPE.ORDER]: 'tagListOrder',
  [BIZ_TYPE.LOGISTICS]: 'tagListLogistics',
  [BIZ_TYPE.IMMEDIATE_ORDER]: 'tagListOrder',
  [BIZ_TYPE.IMMEDIATE_LOGISTICS]: 'tagListLogistics',
}

const initialState: Types.CustomTagState = {
  customTags: [],
  defaultTags: [],
  tagListOrder: [],
  tagListLogistics: [],
  tagListImmediateOrder: [],
  tagListImmediateLogistics: [],
}

const { reducer, actions } = createSlice({
  name: 'customTag',
  initialState,
  reducers: {
    setCustomTags(state, action: PayloadAction<Types.TagItem[]>): Types.CustomTagState {
      return {
        ...state,
        customTags: action.payload,
      }
    },
    setDefaultTags(state, action: PayloadAction<Types.TagItem[]>): Types.CustomTagState {
      return {
        ...state,
        customTags: action.payload,
      }
    },
    setTagList(state, action: PayloadAction<{ bizType: BizType; data: Types.TagListData[] }>): Types.CustomTagState {
      return {
        ...state,
        [StateTagListMap[action.payload.bizType]]: action.payload.data,
      }
    },
  },
})

export const getTagList = (params: Types.TagListPayload) => async (dispatch: AppDispatch) => {
  const response = await Api.admin.post<Types.TagListState>(customTagUrl.tagList, { ...params })
  const { bizCode, bizMessage, message, data } = response
  if (bizCode === 'SUCCESS') {
    dispatch(
      actions.setTagList({
        bizType: params.bizType,
        data: data?.resultList || [],
      })
    )
    return data
  }
  return Promise.reject({ bizCode, bizMessage, message })
}

export const saveTagList = (tagList: Types.TagListData[], bizType: number) => async () => {
  const response = await Api.toast.post<Types.SaveTagListApiState>(customTagUrl.createOrUpdate, {
    reqList: tagList,
    bizType,
  })
  const { bizCode, bizMessage, message, data } = response
  if (bizCode === 'SUCCESS') {
    return data?.isSuccess
  }

  return Promise.reject({ bizCode, bizMessage, message })
}

export const formatTagList = (tagList: Types.TagListData[]): { defaultTags: Types.TagItem[]; customTags: Types.TagItem[] } => {
  return {
    defaultTags: tagList
      .filter((item) => {
        return item.tagType === 1
      })
      .map((item) => {
        const [color, shape] = item.tagCode.split('_')
        return {
          ...item,
          tagShape: shape,
          tagColor: color,
          actionType: ACTION_TYPE.UPDATE,
          validateStatus: '',
          errorMsg: '',
        }
      })
      .sort((a, b) => a.tagName.localeCompare(b.tagName)) as Types.TagItem[],
    customTags: tagList
      .filter((item) => {
        return item.tagType === 2
      })
      .map((item) => {
        const [color, shape] = item.tagCode.split('_')
        return {
          ...item,
          tagShape: shape,
          tagColor: color,
          actionType: ACTION_TYPE.UPDATE,
          validateStatus: '',
          errorMsg: '',
        }
      }),
  }
}

export const parseToSaveData: (tagList: Types.TagItem[]) => Types.TagListData[] = (tagList) => {
  return tagList.map((item) => ({
    tagCode: item.tagCode,
    tagId: item.tagId,
    tagName: trim(item.tagName),
    tagType: item.tagType,
    actionType: item.actionType,
  }))
}

export const genTagItem: (bizType: number) => Types.TagItem = (bizType) => {
  return {
    shape: '',
    tagId: '',
    tagShape: '',
    tagColor: '',
    tagName: '',
    validateStatus: '',
    errorMsg: '',
    tagCode: undefined,
    bizType,
    actionType: ACTION_TYPE.CREATE,
    tagType: 2,
    tagIdFront: uuid(),
  }
}

export const { setCustomTags, setDefaultTags } = actions

export default reducer
